import {
    Component,
    EventEmitter,
    forwardRef,
    Input,
    OnChanges,
    OnDestroy,
    Output,
    SimpleChanges,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { NgbDateStruct } from '@cmps/ng-bootstrap';
import { IOption } from '@cmps/ng-select';
import { CmpsDialog } from '@cmps/ng-uikit';
import { map } from 'rxjs/operators';

import {
    FocusSelectionParams,
    GoalThreshold,
} from '../../coaching-base/config';
import {
    CoachingType,
    CommitmentApprovalDto,
    CommitmentFocusSummarySetDto,
    CommitmentKpi,
    FocusDto,
    GoalStatus,
} from '../../coaching-base/general';
import { SortDirection } from '../../coaching-insight/oa/models/sorting.model';
import { CmpsForm } from '../../coaching-observation/experimental.form';
import { DateProviders } from '../../common/component/datetime-picker';
import {
    CriteriaIdType,
    PerformanceDataViewType,
} from '../../insight/performance-data/metric.pd.model';
import { PerformanceDataService } from '../../insight/performance-data/performance-data.service';
import { disuseStore, useDispatcher, useStore } from '../../store.decorator';
import { PeriodType } from '../../widget/date-range.widget.model';
import { TranslationKeyMap } from '../coaching-commitment.i18n';
import { GoalHistoryCommitmentComponent } from '../commitment-goal-history/history.goal.commitment.component';
import { ObservationFormValue } from '../commitment-observations/observations.commitment.form';
import { GoalsCollection } from '../goals-collection.model';
import { FocusKPI } from '../models/commitment.model';

import {
    clearState,
    fillFocusKPIPerformance,
    goalCommitmentAddStateGoal,
    goalCommitmentMergeChangedFocusKPIs,
    goalCommitmentRetrieveDataCancel,
    goalCommitmentRetrieveGoalEventHistory,
    goalCommitmentRetrieveGoalHistory,
    goalCommitmentSortGoals,
    goalCommitmentUpdateStateGoal,
    loadGoals,
} from './goals.commitment.action';
import {
    CommitmentCloseGoalValue,
    commitmentKPIToGoalFormValueTransformer,
    GoalFormFields,
    goalFormToCommitmentKPITransformer,
    GoalFormValue,
} from './goals.commitment.form';
import goalsReducer from './goals.commitment.reducer';
import { GoalCommitmentState } from './goals.commitment.state';

@Component({
    selector: '[cmpsCommitmentGoals]',
    templateUrl: './goals.commitment.component.html',
    host: {
        class: 'cmps-commitment-goals',
        'data-e2e': 'component-commitment-goals',
    },
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => GoalsCommitmentComponent),
            multi: true,
        },
        DateProviders,
    ],
})
export class GoalsCommitmentComponent
    implements OnChanges, OnDestroy, ControlValueAccessor
{
    state: GoalCommitmentState;
    @Input() storePathPrefix: string;

    @Input() commitmentId = 0;
    @Input() draftId: string;
    @Input() focus: FocusDto;
    @Input() performerId: number;
    @Input() linkedObservations: ObservationFormValue[];
    @Input() lastLinkedObservation: ObservationFormValue;
    @Input() disabled = false;
    @Output() onGoalCloseClick: EventEmitter<CommitmentCloseGoalValue>;

    /** goal to bring into focus after initial load */
    @Input() goalId: number;

    /** generic button was clicked in pillar 3 or not */
    @Input() isGeneric: boolean;

    /** these are also used in pillar 2 */
    @Input() goalThresholds: GoalThreshold[];

    /** commitment approval, if any */
    @Input() approval: CommitmentApprovalDto;

    /** the selected metric. usually from pillar 1 */
    @Input() activeMetricId: number;

    /** placeholder metrics Ids added from non generic flow */
    @Input() placeholderMetrics: number[];

    /** config value of commitment for new focus selector */
    @Input() newFocusSelectorWithYield: boolean;

    @Input() focusSelectionParams: FocusSelectionParams;

    @Output() manageGoal: EventEmitter<boolean>;

    /** @private */ _i18n = TranslationKeyMap; // this breaks convention
    /** @private */ _form: CmpsForm<GoalFormFields>;
    // /** @private */ _goalsCollection: Array<GoalFormValue>;
    // /** @private */ _goalsCollection: GoalsCollection;
    /** @private */ _metricCurrentActualMap: { [key: number]: number };
    /** @private */ _metricNameMap: { [key: number]: string };
    /** @private */ _metricOpts: IOption[];
    /** @private */ _minDate: NgbDateStruct;
    /** @private */ _selected: GoalFormValue;
    // /** @private */ _graphSet: Array<GraphDefinition>;
    /** @private */ _reviewingSharedGoal = false;
    /** @private */ _reviewingUpdatedSharedGoal = false;

    private _onChange: (_: GoalsCollection) => void;
    private _onTouched: () => void;
    // private _onValidatorChange: () => void;

    hideEditButton = false;
    hideCloseDeleteButton: boolean;
    openGoalDetailsInEditMode = true;
    isGoalFromOtherCommitment = false;
    focusSummarySet: CommitmentFocusSummarySetDto;

    i18n = TranslationKeyMap;

    goalReviewCollapsed = false;

    /** @deprecated */
    _isOriginalWithDraftTrackingIdMap: { [trackingId: number]: boolean };

    constructor(
        private readonly _piService: PerformanceDataService,
        private readonly _dialog: CmpsDialog,
    ) {
        this._metricCurrentActualMap = {};
        this._metricNameMap = {};
        this.focusSummarySet = null;
        this.onGoalCloseClick = new EventEmitter();
        this.manageGoal = new EventEmitter();

        this._isOriginalWithDraftTrackingIdMap = {};
    }

    ngOnChanges(args: SimpleChanges): void {
        // TODO FIXME refactor so that this can be called from ngOnInit
        if (!(this as any)._storePath) {
            useStore(
                this,
                `${this.storePathPrefix}.goalCommitment`,
                goalsReducer.goalCommitment,
            );
        }

        if (
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            this.state &&
            this.state.commitmentKpis && // wait for after first call of writeValue() or goalCommitmentInit()
            // eslint-disable-next-line no-prototype-builtins
            (args.hasOwnProperty('draftId') ||
                // eslint-disable-next-line no-prototype-builtins
                args.hasOwnProperty('commitmentId') ||
                // eslint-disable-next-line no-prototype-builtins
                args.hasOwnProperty('isGeneric') ||
                // eslint-disable-next-line no-prototype-builtins
                args.hasOwnProperty('linkedObservations') ||
                // eslint-disable-next-line no-prototype-builtins
                args.hasOwnProperty('placeholderMetrics'))
        ) {
            this._syncPlaceholders();
        }
    }

    ngOnDestroy(): void {
        disuseStore(this);

        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        this._form && this._form.destroy();
        this.onGoalCloseClick.complete();
        this.manageGoal.complete();
        useDispatcher(this, clearState({}));
    }

    registerOnChange(fn: any): void {
        this._onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this._onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        // isDisabled && this._form && this._form.form ? this._form.form.disable({emitEvent: false}) : this._form.form.enable({emitEvent: false});
        this.disabled = isDisabled;
    }

    writeValue(obj: GoalsCollection): void {
        useDispatcher(
            this,
            loadGoals({
                goalsCollection: obj,
                performerId: this.performerId,
                commitmentId: this.commitmentId,
                focus: this.focus,
                linkedObservations: this.linkedObservations,
            }),
        );

        this._reloadGoalHistory();
        this._reloadGoalEventsHistory();
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    stateToProps(prevState?: GoalCommitmentState) {
        if (!prevState) {
            return;
        }

        if (prevState.focusSummarySet !== this.state.focusSummarySet) {
            this.focusSummarySet = this.state.focusSummarySet;
        }

        if (
            prevState.focusKpis &&
            this.state.focusKpis &&
            prevState.focusKpis.length !== this.state.focusKpis.length
        ) {
            this._fillFocusMetricPerformance();
        }

        if (prevState.pending.init && !this.state.pending.init) {
            this._syncPlaceholders();
        }

        if (
            prevState.focusKpis !== this.state.focusKpis ||
            prevState.commitmentKpis !== this.state.commitmentKpis ||
            prevState.goalsFromOtherCommitments !==
                this.state.goalsFromOtherCommitments
        ) {
            this._autoSelectFirstGoal();
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            this._onChange &&
                this._onChange({
                    commitmentKpis: this.state.commitmentKpis,
                    focusKpis: this.state.focusKpis,
                    goalsFromOtherCommitments:
                        this.state.goalsFromOtherCommitments,

                    // see interface property doc
                    initRelated:
                        prevState.pending.init !== this.state.pending.init,
                });
        }
    }

    /** @private */
    onChangeSelectedGoal(
        goal: CommitmentKpi | FocusKPI,
        edit = false,
        hideEditButton = false,
        hideCloseDeleteButton = false,
    ): void {
        const _goal = commitmentKPIToGoalFormValueTransformer(goal);

        if ((goal as FocusKPI).goal_Kpi) {
            _goal.metricName = (goal as FocusKPI).goal_Kpi;
        }

        if (
            !this._selected ||
            _goal.metric ||
            (this._selected.goalId &&
                _goal.goalId &&
                this._selected.goalId !== _goal.goalId)
        ) {
            this._selected = { ..._goal };
            this._reloadGoalHistory();
            this._reloadGoalEventsHistory();
        } else {
            this._selected = null;
        }

        this.openGoalDetailsInEditMode = edit;
        this.hideEditButton = hideEditButton;
        this.hideCloseDeleteButton = hideCloseDeleteButton;

        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        this.isGoalFromOtherCommitment =
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            this.state &&
            this.state.goalsFromOtherCommitments &&
            this.state.goalsFromOtherCommitments.some(
                (e) => e.goalId === _goal.goalId,
            );

        useDispatcher(this, goalCommitmentRetrieveDataCancel({}));
    }

    onRecentGoalsSort(sortDirection: 'asc' | 'desc'): void {
        // TODO refactor out dependence on SortDirection from oa model
        const direction =
            sortDirection === 'asc' ? SortDirection.ASC : SortDirection.DESC;
        useDispatcher(this, goalCommitmentSortGoals({ direction }));
    }

    onNewGoalClick(): void {
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        this._selected = {
            tracking_id: Math.random(),
        } as GoalFormValue;
        this._reviewingSharedGoal = false;
        this._reviewingUpdatedSharedGoal = false;
        this.openGoalDetailsInEditMode = true;
        this.hideEditButton = false;
        this._reloadGoalEventsHistory();
        this._reloadGoalHistory();
        useDispatcher(this, goalCommitmentRetrieveDataCancel({}));
    }

    /** @private */ onGoalHistoryClick(isShowGoalHistory: boolean): void {
        if (
            (isShowGoalHistory && this._selected && this._selected.metric) ||
            this._selected.goalId
        ) {
            this._dialog.open(GoalHistoryCommitmentComponent, {
                data: {
                    goal: this._selected,
                    history: this.state.goalHistory,
                },
            });
        }
    }

    /** @private */ goalCloseClick(option: CommitmentCloseGoalValue): void {
        this.onGoalCloseClick.emit(option);
    }

    /** @private */ onSaveGoalClick(goal: GoalFormValue): void {
        const next = this.state.commitmentKpis; // this._goalsCollection ? {...this._goalsCollection} : { }; //[...this._goalsCollection] : [];

        const index = next.findIndex(
            (e) => e.tracking_id != null && e.tracking_id === goal.tracking_id,
        );
        const matchedCommitmentKpi = next[index];

        const mapgoal = goalFormToCommitmentKPITransformer(
            goal,
            matchedCommitmentKpi,
        );
        if (index === -1) {
            useDispatcher(this, goalCommitmentAddStateGoal({ goal: mapgoal }));
        } else {
            useDispatcher(
                this,
                goalCommitmentUpdateStateGoal({ index, goal: mapgoal }),
            );
        }

        // set the mapped commitment kpi into form value again
        this._selected = commitmentKPIToGoalFormValueTransformer(mapgoal);

        useDispatcher(this, goalCommitmentRetrieveDataCancel({}));
    }

    /** @private */ onUpdateGoalStatusClick(goal: GoalFormValue): void {
        const isPlaceholderMetric = this.state.focusKpis.some(
            (e) => e.metricId === goal.metric,
        );
        const next: Array<FocusKPI | CommitmentKpi> = isPlaceholderMetric
            ? this.state.focusKpis
            : this.state.commitmentKpis;
        const index = next.findIndex(
            (s) =>
                s.metricId === goal.metric ||
                s.tracking_id === goal.tracking_id,
        );
        const matchedCommitmentKpi = next[index];

        const mapgoal = goalFormToCommitmentKPITransformer(
            goal,
            matchedCommitmentKpi,
        );
        if (index !== -1) {
            useDispatcher(
                this,
                goalCommitmentUpdateStateGoal({
                    index,
                    goal: mapgoal,
                    goalStatusId: mapgoal.statusId,
                    isPlaceholderMetric,
                }),
            );
        }
        this.onNewGoalClick();
    }

    private _reloadGoalHistory(): void {
        useDispatcher(
            this,
            goalCommitmentRetrieveGoalHistory({
                commitmentId: this.commitmentId,
                performerId: this.performerId,
                goalId: this._selected ? this._selected.goalId : null,
            }),
        );
    }

    private _reloadGoalEventsHistory(): void {
        useDispatcher(
            this,
            goalCommitmentRetrieveGoalEventHistory({
                commitmentId: this.commitmentId,
                performerId: this.performerId,
                goalId: this._selected ? this._selected.goalId : null,
                linkedObservations: this.linkedObservations,
            }),
        );
    }

    private _syncPlaceholders(): void {
        const metric = this.state.metrics.find(
            (ee) => ee.metricId === this.activeMetricId,
        );
        const metrics = this.placeholderMetrics || [];
        if (
            !this.isGeneric &&
            this.draftId == null &&
            this.commitmentId == null &&
            metric &&
            metric.coachTypeId === CoachingType.UNCOACHED
        ) {
            metrics.push(this.activeMetricId);
        }

        useDispatcher(
            this,
            goalCommitmentMergeChangedFocusKPIs({
                linkedObservations: this.linkedObservations,
                lastLinkedObservation: this.lastLinkedObservation,
                placeholderMetrics: metrics,
            }),
        );
    }

    private _fillFocusMetricPerformance(): void {
        const metricids = this.state.focusKpis
            .filter((a) => !a.actual && !a.percentToTarget && !a.target)
            .map((b) => b.metricId);

        if (!metricids || !metricids.length) return;

        this._piService
            .listDataToMetricData({
                viewType: PerformanceDataViewType.USER_SUPER_GROUP,
                displayNameCriteriaIdType: CriteriaIdType.USER_ID,
                outputCriteriaIdType: CriteriaIdType.USER_ID,
                periodType: PeriodType.ROLLING_30,
                date: new Date(),
                userIdList: [this.performerId],
                metricIdList: metricids,
            })
            .pipe(
                map((data) => {
                    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                    if (data && data.length) {
                        const metrics = data[0].metrics;
                        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                        if (metrics && metrics.length) {
                            useDispatcher(
                                this,
                                fillFocusKPIPerformance({ metrics }),
                            );
                        }
                    }
                }),
            )
            .subscribe();
    }

    private _autoSelectFirstGoal(): void {
        this._checkGoalAutoFocus();

        // auto select the previously selected goal, or auto select the FIRST CommitmentKpi
        const commitmentKpis = this.state.commitmentKpis || [];
        const active = commitmentKpis.filter(
            (q) => q.statusId === GoalStatus.ACTIVE,
        );
        const draft = commitmentKpis.filter(
            (q) => q.statusId === GoalStatus.DRAFT,
        );
        const closed = commitmentKpis.filter(
            (q) => q.statusId === GoalStatus.CLOSED,
        );
        const pending = commitmentKpis.filter(
            (q) =>
                q.statusId === GoalStatus.PRE_APPROVAL ||
                q.statusId === GoalStatus.UPDATING ||
                q.statusId === GoalStatus.PENDING_CLOSE,
        );

        if (
            this._selected &&
            (this._selected.goalId != null || this._selected.metric != null)
        ) {
            const match = [...commitmentKpis]
                .sort((a) => (!a.isOriginalGoal ? -1 : 1))
                .find(
                    (a) =>
                        a.goalId === this._selected.goalId ||
                        a.metricId === this._selected.metric,
                );
            if (match) {
                this.onChangeSelectedGoal(match);
            } else {
                if (active.length) {
                    this.onChangeSelectedGoal(active[0]);
                } else if (commitmentKpis.length) {
                    this.onChangeSelectedGoal(commitmentKpis[0]);
                }
            }
        } else {
            if (
                commitmentKpis.length === 0 &&
                this.state.focusKpis &&
                this.state.focusKpis.length > 0
            ) {
                this.onChangeSelectedGoal(this.state.focusKpis[0]);
            } else if (active.length) {
                this.onChangeSelectedGoal(active[0]);
            } else if (pending.length) {
                this.onChangeSelectedGoal(pending[0]);
            } else if (draft.length) {
                this.onChangeSelectedGoal(draft[0]);
            } else if (closed.length) {
                this.onChangeSelectedGoal(closed[0]);
            }
        }
    }

    // TODO move to reducer?
    private _checkGoalAutoFocus(): void {
        if (this.goalId == null) {
            return;
        }

        const goals: Array<{ goalId?: number }> = [];
        if (this.state.commitmentKpis) {
            goals.push(...this.state.commitmentKpis);
        }
        const g = goals.find((e) => e.goalId === this.goalId);
        // goalId may never be nulled if never found
        if (g) {
            this.onChangeSelectedGoal(g);
            this.goalId = null;
        }
    }
}
